/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from 'src/utils/logicTypes';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IIndividualTermState {
    version: Dtos.Version | null;
    category: Dtos.Category | null;
    parentTerm: Dtos.Term | null;
    term: Dtos.Term | null;
    loadState: IRequestState;
}

interface IInitialTermState extends Record<number, IIndividualTermState> {
}

export interface ITermState {
   term: IInitialTermState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const TermInitialIndividualState: IIndividualTermState = {
    version: null,
    category: null,
    parentTerm: null,
    term: null,
    loadState: {
        state: RequestState.None
    }
};

const TermInitialState: IInitialTermState = {
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useTermSelector: TypedUseSelectorHook<ITermState> = useSelector;

export const TermSelectors = {
    selectTermVersion: (state: ITermState, termId: number) => state.term[termId] ? state.term[termId].version : TermInitialIndividualState.version,
    selectTermCategory: (state: ITermState, termId: number) => state.term[termId] ? state.term[termId].category : TermInitialIndividualState.category,
    selectTermParent: (state: ITermState, termId: number) => state.term[termId] ? state.term[termId].parentTerm : TermInitialIndividualState.parentTerm,
    selectTermTerm: (state: ITermState, termId: number) => state.term[termId] ? state.term[termId].term : TermInitialIndividualState.term,
    selectTerm: (state: ITermState, termId: number) => state.term[termId] ? state.term[termId].term : TermInitialIndividualState.term,
    selectTermLoadState: (state: ITermState, termId: number) => state.term[termId] ? state.term[termId].loadState : TermInitialIndividualState.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class TermReducer extends ImmerReducer<IInitialTermState> {
    public loadById(termId: number) {
        if (!this.draftState[termId]) {
            this.draftState[termId] = {...TermInitialIndividualState};
        }

        this.draftState[termId].loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(version: Dtos.Version, category: Dtos.Category, term: Dtos.Term, parentTerm: Dtos.Term | null) {

        if (!this.draftState[term.id]) {
            this.draftState[term.id] = {...TermInitialIndividualState};
        }

        this.draftState[term.id].version = version;
        this.draftState[term.id].category = category;
        this.draftState[term.id].parentTerm = parentTerm;
        this.draftState[term.id].term = term;

        this.draftState[term.id].loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(termId: number, responseStatus: Dtos.ResponseStatus) {
        if (!this.draftState[termId]) {
            this.draftState[termId] = {...TermInitialIndividualState};
        }

        this.draftState[termId].version = TermInitialIndividualState.version;
        this.draftState[termId].category = TermInitialIndividualState.category;
        this.draftState[termId].parentTerm = TermInitialIndividualState.parentTerm;
        this.draftState[termId].term = TermInitialIndividualState.term;
        this.draftState[termId].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear(termId: number) {
        delete this.draftState[termId];
    }

    public clearAll() {
        return {...TermInitialState};
    }
}

export const TermActions = createActionCreators(TermReducer);
const reducer = createReducerFunction(TermReducer, TermInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const TermApi = {
    loadById: createGetRequest(
        Dtos.GetTermById,
        (id: number) => ({
            id
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const TermLogic = {
    loadById: createLogic({
        type: TermActions.loadById.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<ITermState, typeof TermActions.loadById>, dispatch, done) => {
            try {
                const response = await TermApi.loadById(action.payload);
                dispatch(TermActions.loadSuccess(
                    response.version,
                    response.category,
                    response.term,
                    response.parentTerm
                ));
            }
            catch (error) {
                dispatch(TermActions.loadFailure(action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    TermLogic.loadById
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('term', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/term.ts